import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adjustAccessoryStock, updateAccessoryCatalogue } from '@/services/accessories';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Accessory } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Package, Plus, Minus, Trash2, Loader2, AlertTriangle } from 'lucide-react';

interface StockAdjustmentModalProps {
  accessory: Accessory;
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'adjust' | 'remove';
}

const REMOVAL_PRESETS = [
  'Damaged stock / Defective item',
  'Lost or unaccounted stock',
  'Physical stock count correction',
  'Returned to vendor / Replaced',
  'Entered by mistake / Inventory correction',
  'Other administrative removal',
];

export const StockAdjustmentModal: React.FC<StockAdjustmentModalProps> = ({
  accessory,
  isOpen,
  onClose,
  initialMode = 'adjust',
}) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'adjust' | 'remove'>(initialMode);
  const [changeType, setChangeType] = useState<'add' | 'subtract'>('subtract');
  const [amount, setAmount] = useState<number>(1);
  const [removalPreset, setRemovalPreset] = useState<string>(REMOVAL_PRESETS[0]);
  const [reason, setReason] = useState<string>('');
  const [deactivateIfZero, setDeactivateIfZero] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const isRemovalMode = activeTab === 'remove';
  const effectiveChange = isRemovalMode
    ? -Math.abs(amount)
    : changeType === 'add'
    ? Math.abs(amount)
    : -Math.abs(amount);

  const finalStock = accessory.quantity + effectiveChange;
  const isFinalStockZero = finalStock === 0;

  const finalReasonText = isRemovalMode
    ? reason.trim()
      ? `${removalPreset}: ${reason.trim()}`
      : removalPreset
    : reason.trim();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!finalReasonText.trim()) {
        throw new Error('Please specify a reason for this stock change.');
      }
      if (finalStock < 0) {
        throw new Error('Stock quantity cannot be reduced below zero.');
      }
      if (effectiveChange === 0) {
        throw new Error('Quantity change cannot be zero.');
      }

      await adjustAccessoryStock({
        accessory_id: accessory.id,
        quantity_change: effectiveChange,
        reason: finalReasonText.trim(),
      });

      if (isFinalStockZero && deactivateIfZero) {
        await updateAccessoryCatalogue(accessory.id, {
          is_active: false,
          is_public: false,
        });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.detail(accessory.id) });
      onClose();
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  const handleRemoveAll = () => {
    setAmount(accessory.quantity);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    mutation.mutate();
  };

  return (
    <Modal
      isOpen={isOpen}
      title={isRemovalMode ? `Remove Stock — ${accessory.name}` : `Adjust Stock — ${accessory.name}`}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorMsg && <ErrorAlert message={errorMsg} />}

        {/* Tab Switcher */}
        <div className="flex border-b border-brand-border text-xs font-semibold">
          <button
            type="button"
            onClick={() => setActiveTab('adjust')}
            className={`pb-2 px-4 transition-colors ${
              activeTab === 'adjust'
                ? 'border-b-2 border-brand-green text-brand-green font-bold'
                : 'text-brand-muted hover:text-brand-black'
            }`}
          >
            General Adjustment (+/-)
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('remove');
              setAmount(Math.min(1, accessory.quantity) || 1);
            }}
            className={`pb-2 px-4 transition-colors flex items-center space-x-1.5 ${
              activeTab === 'remove'
                ? 'border-b-2 border-brand-red text-brand-red font-bold'
                : 'text-brand-muted hover:text-brand-black'
            }`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>Remove Stock / Write-Off (-)</span>
          </button>
        </div>

        {/* Stock Level Preview Box */}
        <div className="bg-brand-bg p-3 rounded-lg border border-brand-border flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-wider">Current Stock</p>
            <p className="text-lg font-bold text-brand-black">{accessory.quantity} units</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-wider">Resulting Stock</p>
            <p className={`text-lg font-bold ${finalStock < 0 ? 'text-brand-red' : finalStock === 0 ? 'text-amber-600' : 'text-brand-green'}`}>
              {finalStock} units
            </p>
          </div>
        </div>

        {/* Adjust Mode: Add vs Subtract Toggle */}
        {!isRemovalMode && (
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Adjustment Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setChangeType('add')}
                className={`py-2 px-3 rounded-md text-xs font-semibold flex items-center justify-center space-x-2 border transition-all ${
                  changeType === 'add'
                    ? 'bg-green-50 border-brand-green text-brand-green ring-1 ring-brand-green'
                    : 'bg-white border-brand-border text-brand-black'
                }`}
              >
                <Plus className="w-4 h-4" />
                <span>Add Stock (+)</span>
              </button>

              <button
                type="button"
                onClick={() => setChangeType('subtract')}
                className={`py-2 px-3 rounded-md text-xs font-semibold flex items-center justify-center space-x-2 border transition-all ${
                  changeType === 'subtract'
                    ? 'bg-red-50 border-brand-red text-brand-red ring-1 ring-brand-red'
                    : 'bg-white border-brand-border text-brand-black'
                }`}
              >
                <Minus className="w-4 h-4" />
                <span>Deduct Stock (-)</span>
              </button>
            </div>
          </div>
        )}

        {/* Removal Mode: Fast Shortcuts */}
        {isRemovalMode && (
          <div className="flex items-center justify-between p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs">
            <div className="flex items-center space-x-2 text-red-900">
              <AlertTriangle className="w-4 h-4 text-brand-red shrink-0" />
              <span className="font-medium">Direct stock deduction with audit log.</span>
            </div>
            {accessory.quantity > 0 && (
              <button
                type="button"
                onClick={handleRemoveAll}
                className="px-2.5 py-1 bg-white border border-red-300 text-brand-red font-bold rounded text-[11px] hover:bg-red-100 transition-colors"
              >
                Remove All ({accessory.quantity})
              </button>
            )}
          </div>
        )}

        {/* Quantity Change Input */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider">
              {isRemovalMode ? 'Quantity to Remove' : `Units to ${changeType === 'add' ? 'Add' : 'Deduct'}`}{' '}
              <span className="text-brand-red">*</span>
            </label>
            {accessory.quantity > 0 && !isRemovalMode && changeType === 'subtract' && (
              <button
                type="button"
                onClick={handleRemoveAll}
                className="text-[11px] text-brand-red font-semibold hover:underline"
              >
                Deduct all ({accessory.quantity})
              </button>
            )}
          </div>
          <input
            type="number"
            min={1}
            max={isRemovalMode || changeType === 'subtract' ? accessory.quantity : undefined}
            value={amount}
            onChange={(e) => setAmount(Math.max(1, parseInt(e.target.value) || 0))}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-sm font-semibold text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
          />
        </div>

        {/* Removal Category Presets (if in removal mode) */}
        {isRemovalMode && (
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Removal Category <span className="text-brand-red">*</span>
            </label>
            <select
              value={removalPreset}
              onChange={(e) => setRemovalPreset(e.target.value)}
              className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs font-semibold text-brand-black focus:ring-2 focus:ring-brand-green"
            >
              {REMOVAL_PRESETS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Required Reason Notes */}
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            {isRemovalMode ? 'Specific Reason / Details' : 'Adjustment Reason'} <span className="text-brand-red">*</span>
          </label>
          <input
            type="text"
            placeholder={
              isRemovalMode
                ? 'e.g. Broken packaging, Water damage during transit, Inventory reconciliation'
                : 'e.g. Received shipment, Stock audit count correction, Damaged stock'
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
            required={!isRemovalMode}
          />
          <p className="text-[11px] text-brand-muted mt-1">
            All adjustments are permanently logged in stock movements and audit records.
          </p>
        </div>

        {/* Deactivate Option when resulting stock is 0 */}
        {isFinalStockZero && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs space-y-1">
            <label className="flex items-center space-x-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={deactivateIfZero}
                onChange={(e) => setDeactivateIfZero(e.target.checked)}
                className="w-4 h-4 text-brand-green rounded border-brand-border focus:ring-brand-green"
              />
              <span className="font-bold text-amber-900">
                Deactivate this accessory item (stock reaches 0)
              </span>
            </label>
            <p className="text-[11px] text-amber-700 pl-6">
              Hides this product from the POS sales picker and public catalogue while preserving full transaction and cost history.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="px-4 py-2 text-xs font-medium text-brand-muted hover:text-brand-black transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || !finalReasonText.trim() || finalStock < 0 || accessory.quantity === 0 && effectiveChange <= 0}
            className={`inline-flex items-center space-x-1.5 px-4 py-2 text-xs font-bold rounded-md transition-colors shadow-sm disabled:opacity-50 ${
              isRemovalMode
                ? 'bg-brand-red text-white hover:bg-red-700'
                : 'bg-brand-green text-white hover:bg-brand-green-hover'
            }`}
          >
            {mutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : isRemovalMode ? (
              <Trash2 className="w-3.5 h-3.5" />
            ) : (
              <Package className="w-3.5 h-3.5" />
            )}
            <span>
              {isRemovalMode
                ? amount >= accessory.quantity
                  ? 'Remove All Stock'
                  : `Remove ${amount} Units`
                : 'Confirm Adjustment'}
            </span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
